import { useEffect, useState, useMemo, useRef } from 'react';

import {
    AspectRatio,
    Box,
    Button,
    Card,
    Checkbox,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    Icon,
    Image,
    Link,
    ListItem,
    Select,
    SlideFade,
    Spacer,
    Text,
    UnorderedList,
    useBreakpointValue,
    Tag,
    Table,
    TableContainer,
    Tbody,
    Td,
    Th,
    Thead,
    Tr
} from '@chakra-ui/react';

import { DataTable } from './Misc/DataTable';
import { SongDataItem } from './Misc/SongDataItem';

export function SelectColumnFilter({
  column: { filterValue, setFilter, preFilteredRows, id }
}) {
  // Calculate the options for filtering
  // using the preFilteredRows
  const options = useMemo(() => {
    const options = new Set();
    preFilteredRows.forEach((row) => {
      options.add(row.values[id]);
    });
    return [...options.values()];
  }, [id, preFilteredRows]);

  return (
    <Select
      size={"xs"}
      value={filterValue}
      onChange={(e) => {
        setFilter(e.target.value || undefined);
      }}
      variant={"outline"}
    >
      <option value="">All</option>
      {options.map((option, i) => (
        <option key={i} value={option}>
          {option}
        </option>
      ))}
    </Select>
  );
}

export function SongData({eventConfig, getCharts}) {

    const isMobile = useBreakpointValue(
        {
          "base": ()=> true,
          "xl": ()=>  false
        }
    );     

    const getLengthTime = (length) => {
        let minutes = "" + length;
        minutes = length.split(".")[0];

        let hours = 0;

        if (minutes > 59) {
            hours = Math.floor(minutes / 60);
            minutes = minutes - (hours * 60);
            minutes = minutes < 10
                ? "0" + minutes
                : minutes;            
        }

        let seconds = "0." + parseInt(length.split(".")[1]);

        seconds = Math.floor(seconds * 60);

        seconds = seconds < 10
            ? "0" + seconds
            : seconds;

        const lengthString = hours > 0
            ? `${hours}:${minutes}:${seconds}`
            : `${minutes}:${seconds}`;

        return lengthString;
    }

    const srpgShort = eventConfig["eventShort"];
    const hasDoubles = eventConfig["hasItgDouble"] || eventConfig["hasPumpDouble"];

    const [chartData, setChartData] = useState(false);

    const [minSelectedBlock, setMinSelectedBlock] = useState(false);
    const [maxSelectedBlock, setMaxSelectedBlock] = useState(false);
    const [minSelectedBpm, setMinSelectedBpm] = useState(false);
    const [maxSelectedBpm, setMaxSelectedBpm] = useState(false);    
    const [blockList, setBlockList] = useState([]);
    const [bpmList, setBpmList] = useState([]);
    const [typeSelected, setTypeSelected] = useState("all");    
    const [marathonChecked, setMarathonChecked] = useState(false);

    const [songDataFilter, setSongDataFilter] = useState({});

    const columns = useMemo(
        () => 
            [
                {
                    Header: isMobile() ? "Diff" : "Rating",
                    accessor: `${srpgShort}_charts_rating`,
                    Filter: "",
                    filter: "between",
                    sortType: "number",
                    width: 55,
                },            
                {
                    Header: "BPM",
                    accessor: `${srpgShort}_charts_bpm`,
                    Filter: "",
                    filter: "between",
                    sortType: "number",                    
                    width: 60,
                    show: isMobile() ? false : true
                },                   
                {
                    Header: "Song",
                    accessor: `${srpgShort}_charts_song`,
                    Filter: "",
                    filter: "",
                    width: 140
                },     
                {
                    Header: "TPX",
                    accessor: "chart_tpx_preview_100",
                    Filter: "",
                    filter: "",
                    sortType: "number",
                    width: 75
                },
                {
                    Header: "LPX",
                    accessor: "chart_lpx_preview_100",
                    Filter: "",
                    filter: "",
                    sortType: "number",
                    width: 75
                },     
                {
                    Header: isMobile() ? "BPM" : "BPM Tier",
                    accessor: `${srpgShort}_charts_speedtierbase`,
                    Filter: "",
                    filter: "between",
                    sortType: "number",                    
                    width: 60,
                    show: false
                },
                {
                    Header: "Faction",
                    accessor: `${srpgShort}_charts_faction`,
                    Filter: "",
                    filter: "includesValue", //lets us filter against multiple factions
                    sortType: "number",                    
                    width: 60,
                    show: false
                },                
            ]
            , [isMobile()]
    );

    const chartDataRequest = async () => {
        const newChartData = await getCharts();

        let newMinBlock = false;
        let newMaxBlock = false;
        let newMinBpm = false;
        let newMaxBpm = false;

        let newBlockList = [];
        let newBpmList = [];

        newChartData.forEach((chart)=>{
            const block = parseInt(chart?.[`${srpgShort}_charts_rating`]);
            const bpm = parseInt(chart?.[`${srpgShort}_charts_speedtierbase`]);

            if (!newMinBlock || block < newMinBlock) {
                newMinBlock = block;
            }

            if (!newMaxBlock || block > newMaxBlock) {
                newMaxBlock = block;
            }        

            if (!newMinBpm || bpm < newMinBpm) {
                newMinBpm = bpm;
            }

            if (!newMaxBpm || bpm) {
                newMaxBpm = bpm;
            }

            if (!newBlockList.includes(block)) {
                newBlockList.push(block);
            }

            if (!newBpmList.includes(bpm)) {
                newBpmList.push(bpm);
            }

            setMinSelectedBlock(newMinBlock);
            setMaxSelectedBlock(newMaxBlock);
            setMinSelectedBpm(newMinBpm);
            setMaxSelectedBpm(newMaxBpm);
        });

        newBlockList.sort((a, b) => (a - b));        
        newBpmList.sort((a, b) => (a - b));        

        setBlockList(newBlockList);
        setBpmList(newBpmList);

        setChartData([...newChartData]);
    };

    const updateSongDataFilter = (id=false, value=false, doSet=true, passedSongDataFilter=false) => {
        const newSongDataFilter = passedSongDataFilter ? {...passedSongDataFilter} : {...songDataFilter};

        if (value === "") {
            delete newSongDataFilter[id];
        } else {
            newSongDataFilter[id] = value;
        }

        if (doSet) {
            setSongDataFilter(newSongDataFilter);
        } else {
            return newSongDataFilter;
        }
    };    

    const VerticalPointTableRow = ({score, scoreBgColor, points}) => {
        return (
            <Tr>
                <Td fontWeight="bold" textAlign="center" bgColor={scoreBgColor}>{score}%</Td>
                <Td textAlign="center">{points}</Td>
            </Tr>
        );
    }

    const HorizontalPointTableCell = ({score, scoreBgColor}) => {
        return (
            <Td fontWeight="bold" textAlign="center" bgColor={scoreBgColor}>{score}%</Td>
        );
    }

    const initialState = useMemo(()=>{
        return { 
            pageIndex: 0, 
            pageSize: 10,
            sortBy: [
                {
                    id: `${srpgShort}_charts_rating`,
                    desc: false
                },
                {
                    id: "chart_tpx_preview_100",
                    desc: false
                },                
            ]
        }
    }, []);

    const extraChartData = (data) => {
        return (
            <>
                {
                    isMobile() && (
                        <>
                            {
                                data["chart_tpx_preview_100"] > 0 && (
                                    <Box maxWidth="300px" marginBottom={3}>
                                        <Heading size="md" marginBottom={3}>TPX</Heading>
                                        <Table size="sm">
                                            <Thead>
                                                <Tr>
                                                    <Th textAlign="center">Score</Th>
                                                    <Th textAlign="center">Points</Th>                                
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                <VerticalPointTableRow score="100" scoreBgColor="#0B3D0B" points={data["chart_tpx_preview_100"]}/>
                                                <VerticalPointTableRow score="99" scoreBgColor="#145A32" points={data["chart_tpx_preview_99"]}/>
                                                <VerticalPointTableRow score="95" scoreBgColor="#1E8449" points={data["chart_tpx_preview_95"]}/>
                                                <VerticalPointTableRow score="90" scoreBgColor="#2E7D32" points={data["chart_tpx_preview_90"]}/>
                                                <VerticalPointTableRow score="85" scoreBgColor="#3E6E1E" points={data["chart_tpx_preview_85"]}/>
                                                <VerticalPointTableRow score="80" scoreBgColor="#5A6B1E" points={data["chart_tpx_preview_80"]}/>
                                                <VerticalPointTableRow score="75" scoreBgColor="#A07911" points={data["chart_tpx_preview_75"]}/>
                                                <VerticalPointTableRow score="70" scoreBgColor="#C25B06" points={data["chart_tpx_preview_70"]}/>
                                                <VerticalPointTableRow score="65" scoreBgColor="#A93226" points={data["chart_tpx_preview_65"]}/>
                                                <VerticalPointTableRow score="60" scoreBgColor="#7B241C" points={data["chart_tpx_preview_60"]}/>
                                            </Tbody>
                                        </Table>
                                    </Box>
                                )
                            }
                            {
                                data["chart_lpx_preview_100"] > 0 && (
                                    <Box maxWidth="300px" marginBottom={3}>
                                        <Heading size="md" marginBottom={3}>LPX</Heading>
                                        <Table size="sm">
                                            <Thead>
                                                <Tr>
                                                    <Th textAlign="center">Score</Th>
                                                    <Th textAlign="center">Points</Th>                                
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                <VerticalPointTableRow score="100" scoreBgColor="#0B3D0B" points={data["chart_lpx_preview_100"]}/>
                                                <VerticalPointTableRow score="99" scoreBgColor="#145A32" points={data["chart_lpx_preview_99"]}/>
                                                <VerticalPointTableRow score="95" scoreBgColor="#1E8449" points={data["chart_lpx_preview_95"]}/>
                                                <VerticalPointTableRow score="90" scoreBgColor="#2E7D32" points={data["chart_lpx_preview_90"]}/>
                                                <VerticalPointTableRow score="85" scoreBgColor="#3E6E1E" points={data["chart_lpx_preview_85"]}/>
                                                <VerticalPointTableRow score="80" scoreBgColor="#5A6B1E" points={data["chart_lpx_preview_80"]}/>
                                                <VerticalPointTableRow score="75" scoreBgColor="#A07911" points={data["chart_lpx_preview_75"]}/>
                                                <VerticalPointTableRow score="70" scoreBgColor="#C25B06" points={data["chart_lpx_preview_70"]}/>
                                                <VerticalPointTableRow score="65" scoreBgColor="#A93226" points={data["chart_lpx_preview_65"]}/>
                                                <VerticalPointTableRow score="60" scoreBgColor="#7B241C" points={data["chart_lpx_preview_60"]}/>
                                            </Tbody>
                                        </Table>
                                    </Box>  
                                )
                            }                                                
                        </>
                    )
                }
                {
                    !isMobile() && (
                        <>
                            {
                                data["chart_tpx_preview_100"] > 0 && (
                                    <Box marginBottom={3}>
                                        <Heading size="md" marginBottom={3}>TPX</Heading>
                                        <Table size="sm">
                                            <Tbody>
                                                <Tr>
                                                    <Th textAlign="center">Score</Th>
                                                    <HorizontalPointTableCell score="100" scoreBgColor="#0B3D0B"/>
                                                    <HorizontalPointTableCell score="99" scoreBgColor="#145A32"/>
                                                    <HorizontalPointTableCell score="95" scoreBgColor="#1E8449"/>
                                                    <HorizontalPointTableCell score="90" scoreBgColor="#2E7D32"/>
                                                    <HorizontalPointTableCell score="85" scoreBgColor="#3E6E1E"/>
                                                    <HorizontalPointTableCell score="80" scoreBgColor="#5A6B1E"/>
                                                    <HorizontalPointTableCell score="75" scoreBgColor="#A07911"/>
                                                    <HorizontalPointTableCell score="70" scoreBgColor="#C25B06"/>
                                                    <HorizontalPointTableCell score="65" scoreBgColor="#A93226"/>
                                                    <HorizontalPointTableCell score="60" scoreBgColor="#7B241C"/>
                                                </Tr>
                                                <Tr>
                                                    <Th textAlign="center">Points</Th>
                                                    <Td textAlign="center">{data["chart_tpx_preview_100"]}</Td>
                                                    <Td textAlign="center">{data["chart_tpx_preview_99"]}</Td>
                                                    <Td textAlign="center">{data["chart_tpx_preview_95"]}</Td>
                                                    <Td textAlign="center">{data["chart_tpx_preview_90"]}</Td>
                                                    <Td textAlign="center">{data["chart_tpx_preview_85"]}</Td>
                                                    <Td textAlign="center">{data["chart_tpx_preview_80"]}</Td>
                                                    <Td textAlign="center">{data["chart_tpx_preview_75"]}</Td>
                                                    <Td textAlign="center">{data["chart_tpx_preview_70"]}</Td>
                                                    <Td textAlign="center">{data["chart_tpx_preview_65"]}</Td>
                                                    <Td textAlign="center">{data["chart_tpx_preview_60"]}</Td>
                                                </Tr>
                                            </Tbody>
                                        </Table>
                                    </Box>                                
                                )
                            }                        
                            {
                                data["chart_lpx_preview_100"] > 0 && (
                                    <Box marginBottom={3}>
                                        <Heading size="md" marginBottom={3}>LPX</Heading>
                                        <Table size="sm">
                                            <Tbody>
                                                <Tr>
                                                    <Th textAlign="center">Score</Th>
                                                    <HorizontalPointTableCell score="100" scoreBgColor="#0B3D0B"/>
                                                    <HorizontalPointTableCell score="99" scoreBgColor="#145A32"/>
                                                    <HorizontalPointTableCell score="95" scoreBgColor="#1E8449"/>
                                                    <HorizontalPointTableCell score="90" scoreBgColor="#2E7D32"/>
                                                    <HorizontalPointTableCell score="85" scoreBgColor="#3E6E1E"/>
                                                    <HorizontalPointTableCell score="80" scoreBgColor="#5A6B1E"/>
                                                    <HorizontalPointTableCell score="75" scoreBgColor="#A07911"/>
                                                    <HorizontalPointTableCell score="70" scoreBgColor="#C25B06"/>
                                                    <HorizontalPointTableCell score="65" scoreBgColor="#A93226"/>
                                                    <HorizontalPointTableCell score="60" scoreBgColor="#7B241C"/>
                                                </Tr>
                                                <Tr>
                                                    <Th textAlign="center">Points</Th>
                                                    <Td textAlign="center">{data["chart_lpx_preview_100"]}</Td>
                                                    <Td textAlign="center">{data["chart_lpx_preview_99"]}</Td>
                                                    <Td textAlign="center">{data["chart_lpx_preview_95"]}</Td>
                                                    <Td textAlign="center">{data["chart_lpx_preview_90"]}</Td>
                                                    <Td textAlign="center">{data["chart_lpx_preview_85"]}</Td>
                                                    <Td textAlign="center">{data["chart_lpx_preview_80"]}</Td>
                                                    <Td textAlign="center">{data["chart_lpx_preview_75"]}</Td>
                                                    <Td textAlign="center">{data["chart_lpx_preview_70"]}</Td>
                                                    <Td textAlign="center">{data["chart_lpx_preview_65"]}</Td>
                                                    <Td textAlign="center">{data["chart_lpx_preview_60"]}</Td>
                                                </Tr>
                                            </Tbody>
                                        </Table>
                                    </Box>                                        
                                )
                            }                        
                        </>
                    )
                }
                <Flex flexWrap={{base: "wrap", xl: "nowrap"}} flexDirection="row" maxWidth={{base: "300px", xl: "fit-content"}}>
                    <Box marginBottom={3} marginRight={3}>
                        <Heading size="md" marginBottom={3}>Song Data</Heading>
                        <Flex direction="column" wrap="wrap" >
                            <SongDataItem headerText="Song" valueText={data[`${srpgShort}_charts_song`]} addShift={true}/>
                            <SongDataItem headerText="Rating" valueText={data[`${srpgShort}_charts_rating`]} addShift={true}/>
                            <SongDataItem headerText="BPM" valueText={data[`${srpgShort}_charts_bpm`]} addShift={true}/>
                            <SongDataItem headerText="Types" valueText={data[`${srpgShort}_charts_faction`] == 4 ? "Double Only" : "Single & Double"} addShift={true}/>
                            <SongDataItem headerText="Stepartist" valueText={data[`${srpgShort}_charts_stepartist`]} addShift={true}/>
                            <SongDataItem headerText="Pack" valueText={data[`${srpgShort}_charts_pack`]} addShift={true}/>
                            <SongDataItem headerText="Steps" valueText={data[`${srpgShort}_charts_steps`]} addShift={true}/>
                            <SongDataItem headerText="16ths" valueText={`${data[`${srpgShort}_charts_16ths`]} measures`} addShift={true}/>
                            {
                                data[`${srpgShort}_charts_24ths`] > 0 && (
                                    <SongDataItem headerText="24ths" valueText={`${data[`${srpgShort}_charts_24ths`]} measures`} addShift={true}/>
                                )
                            }      
                            {
                                data[`${srpgShort}_charts_32nds`] > 0 && (
                                    <SongDataItem headerText="32nds" valueText={`${data[`${srpgShort}_charts_32nds`]} measures`} addShift={true}/>
                                )
                            }                                    
                            <SongDataItem headerText="Break" valueText={`${data[`${srpgShort}_charts_break`]} measures`} addShift={true}/>                
                            <SongDataItem headerText="Stream %" valueText={`${(data[`${srpgShort}_charts_adjstream`]*100).toFixed(2)}%`} addShift={true}/>
                        </Flex>
                    </Box>
                    <Box>
                        <Heading size="md" marginBottom={3}>Breakdown</Heading>
                        <Card padding={2} maxWidth={{base: "275px", xl: "fit-content"}} bgColor="whiteAlpha.300" style={{textWrapMode: "wrap"}}>
                            {data[`${srpgShort}_charts_breakdown`]}
                        </Card>                        
                    </Box>
                </Flex>
            </>
        );
    }

    useEffect(()=> {
        let newSongDataFilter = updateSongDataFilter(`${srpgShort}_charts_rating`, "", false);
        newSongDataFilter = updateSongDataFilter(`${srpgShort}_charts_bpm`, "", false, newSongDataFilter);
        newSongDataFilter = updateSongDataFilter(`${srpgShort}_charts_faction`, [], false, newSongDataFilter);

        if (minSelectedBlock && maxSelectedBlock) {
            newSongDataFilter = updateSongDataFilter(`${srpgShort}_charts_rating`, [parseInt(minSelectedBlock), parseInt(maxSelectedBlock)], false, newSongDataFilter);
        }

        if (minSelectedBpm && maxSelectedBpm) {
            newSongDataFilter = updateSongDataFilter(`${srpgShort}_charts_speedtierbase`, [parseInt(minSelectedBpm), parseInt(maxSelectedBpm)], false, newSongDataFilter);
        }
        
        if (typeSelected == "double") {
            newSongDataFilter = updateSongDataFilter(`${srpgShort}_charts_faction`, [4], false, newSongDataFilter);
        } else if (typeSelected == "single") {
            newSongDataFilter = updateSongDataFilter(`${srpgShort}_charts_faction`, [0,1,2,3], false, newSongDataFilter);
        }
        
        //you can't actually have a doubles specific marathon so let's just give bunk filtering so I don't have to handle some other way
        if (marathonChecked) {
            if (typeSelected == "double") {
                newSongDataFilter = updateSongDataFilter(`${srpgShort}_charts_faction`, [999], false, newSongDataFilter);
            } else {
                newSongDataFilter = updateSongDataFilter(`${srpgShort}_charts_faction`, [1], false, newSongDataFilter);
            }
        }         

        setSongDataFilter(newSongDataFilter);
    }, [minSelectedBlock, maxSelectedBlock, minSelectedBpm, maxSelectedBpm, typeSelected, marathonChecked]);

    useEffect(()=> {
        chartDataRequest();
    }, []);

    try {
        return (
            <>
                <Card mb={4} padding={4} maxWidth={"600px"}>
                    <Heading size="md" mb={3}>Filters</Heading>       
                    <Flex
                        flexFlow="row"
                        flexWrap="wrap"
                        alignSelf="center"
                        gap="15px"
                    >
                        <Flex>
                            <Box pt="7px" minWidth="60px" textAlign="right" pr="5px">
                                Ratings:
                            </Box>                          
                            <FormControl width="unset" variant="floatingCard">                          
                                <Select                                
                                    minWidth="85px"
                                    value={minSelectedBlock}
                                    cursor="pointer"
                                    onChange={(selection)=>{
                                        setMinSelectedBlock(selection.target.value)
                                    }}
                                >
                                    {blockList.map((minBlock, i) => (
                                        <option disabled={minBlock > maxSelectedBlock ? true : false} key={i} value={minBlock}>
                                            {minBlock}
                                        </option>
                                    ))}
                                </Select>
                                <FormLabel>Min</FormLabel>        
                            </FormControl>
                            <Box mt="6px" pl="5px" pr="5px">
                                to
                            </Box>
                            <FormControl width="unset" variant="floatingCard">
                                <Select                                
                                    minWidth="85px"
                                    value={maxSelectedBlock}
                                    cursor="pointer"
                                    onChange={(selection)=>{
                                        setMaxSelectedBlock(selection.target.value)
                                    }}
                                >
                                    {blockList.map((maxBlock, i) => (
                                        <option disabled={maxBlock < minSelectedBlock ? true : false} key={i} value={maxBlock}>
                                            {maxBlock}
                                        </option>
                                    ))}
                                </Select>
                                <FormLabel>Max</FormLabel>        
                            </FormControl>
                        </Flex>
                        <Flex>
                            <Box pt="7px" minWidth="60px" textAlign="right" pr="5px">
                                BPMs:
                            </Box>                          
                            <FormControl width="unset" variant="floatingCard">                          
                                <Select                                
                                    minWidth="85px"
                                    value={minSelectedBpm}
                                    cursor="pointer"
                                    onChange={(selection)=>{
                                        setMinSelectedBpm(selection.target.value)
                                    }}
                                >
                                    {bpmList.map((minBpm, i) => (
                                        <option disabled={minBpm > maxSelectedBpm ? true : false} key={i} value={minBpm}>
                                            {minBpm}
                                        </option>
                                    ))}
                                </Select>
                                <FormLabel>Min</FormLabel>        
                            </FormControl>
                            <Box mt="6px" pl="5px" pr="5px">
                                to
                            </Box>
                            <FormControl width="unset" variant="floatingCard">
                                <Select                                
                                    minWidth="85px"
                                    value={maxSelectedBpm}
                                    cursor="pointer"
                                    onChange={(selection)=>{
                                        setMaxSelectedBpm(selection.target.value)
                                    }}
                                >
                                    {bpmList.map((maxBpm, i) => (
                                        <option disabled={maxBpm < minSelectedBpm ? true : false} key={i} value={maxBpm}>
                                            {maxBpm}
                                        </option>
                                    ))}
                                </Select>
                                <FormLabel>Max</FormLabel>        
                            </FormControl>
                        </Flex>
                        <Flex wrap="wrap">
                            <Flex>
                                <Box pt="7px" minWidth="60px" textAlign="right" pr="5px">
                                    Type:
                                </Box>                               
                                <FormControl width="unset" variant="floatingCard">                                                          
                                    <Select                 
                                        minWidth="195px"
                                        value={typeSelected}
                                        cursor="pointer"
                                        onChange={(selection)=>{
                                            setTypeSelected(selection.target.value)
                                        }}
                                    >
                                        <option key="0" value="">All</option>
                                        <option key="1" value="single">Single Only</option>
                                        <option key="2" value="double">Double Only</option>
                                    </Select>
                                    <FormLabel>Single/Double</FormLabel>        
                                </FormControl>                                  
                            </Flex>                          
                            <Checkbox
                                isChecked={marathonChecked}
                                onChange={(selection)=>{
                                    setMarathonChecked(marathonChecked ? false : true);
                                }}
                                ml={isMobile() ? "60px" : "75px"}
                                mt={isMobile() ? "10px" : "0px"}
                            >
                                Marathons Only
                            </Checkbox>
                        </Flex>

                    </Flex>                    
                </Card>
                <Box width="100%">
                    {
                        (chartData && chartData?.length > 0)
                            ?                    
                                <DataTable data={chartData} columns={columns} initialState={{...initialState} } customFilters={{...songDataFilter}} subRowContents={extraChartData}/>
                            : chartData !== false
                                ?
                                    <Heading size="md">No song data found!</Heading>
                                :
                                    <Heading size="md">Please wait...</Heading>     
                    }                           
                </Box>
            </>

        );
    } catch (e) {
        console.error("SongData render error: ", e);
    }
}